import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  Archive,
  Calendar,
  CalendarDays,
  CalendarRange,
  CheckCircle2,
  Clock,
  CornerUpLeft,
  Edit,
  ExternalLink,
  Eye,
  FileStack,
  Grid3X3,
  List,
  ListTodo,
  Mail,
  MailOpen,
  MailX,
  Moon,
  Plus,
  Redo2,
  RefreshCw,
  Settings,
  Sparkles,
  Star,
  Sun,
  Trash2,
  Undo2,
  Users,
  XCircle,
} from 'lucide-react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
} from '@/components/ui/command';
import { useCommandPaletteStore } from '@/stores/commandPaletteStore';
import { useSearchStore, type SearchResultItem } from '@/stores/searchStore';
import { useUIStore, type MailView, type Workspace } from '@/stores/uiStore';
import { navigateToMailView } from '@/features/mailbox/mailViewNavigation';
import { useEmailStore } from '@/stores/emailStore';
import { useEmailActionsStore } from '@/stores/emailActionsStore';
import { keyboardRegistry } from '@/features/keyboard-shortcuts/keyboardRegistry';
import { useTodoStore } from '@/stores/todoStore';
import { useTodoActionsStore } from '@/stores/todoActionsStore';
import { useTodoViewStore } from '@/stores/todoViewStore';
import type { TodoPrimaryView } from '@/types/todo';
import { useAccountStore } from '@/stores/accountStore';
import { useFolderStore } from '@/stores/folderStore';
import { useBulkOperationsStore } from '@/stores/bulkOperationsStore';
import { useSenderGroupingStore } from '@/stores/senderGroupingStore';
import { useCalendarViewStore } from '@/stores/calendarViewStore';
import { useCalendarModalStore } from '@/stores/calendarModalStore';
import { navigateToTask, navigateToCalendarEvent, navigateToMail } from '@/lib/searchNavigation';
import { mailEvents } from '@/events/mailEvents';
import { useUndoStore } from '@/stores/undoStore';
import { safePushState } from '@/lib/iosPwaNavigation';

interface ActionItem {
  id: string;
  label: string;
  icon: LucideIcon;
  shortcut?: string;
  onSelect: () => void;
}

export function CommandPalette() {
  const isOpen = useCommandPaletteStore((state) => state.isOpen);
  const anchorRect = useCommandPaletteStore((state) => state.anchorRect);
  const close = useCommandPaletteStore((state) => state.close);

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  const searchResults = useSearchStore((state) => state.results);
  const relatedResults = useSearchStore((state) => state.related);
  const searchLoading = useSearchStore((state) => state.loading);
  const performSearch = useSearchStore((state) => state.performSearch);

  // Snapshot/restore the SHARED search store around the palette's lifetime
  // (X2): the palette's type-ahead writes the very store a committed search
  // view reads. Capture whatever was there when the palette opened and restore
  // it on dismiss — Cmd+K over an open search view no longer wipes it to "No
  // results" with an empty query, and dismissal never touches the mail reader
  // or sender group (X1).
  type SearchSnapshot = Pick<
    ReturnType<typeof useSearchStore.getState>,
    'query' | 'tokens' | 'results' | 'related' | 'hasMore' | 'error'
  >;
  const searchSnapshotRef = useRef<SearchSnapshot | null>(null);
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const s = useSearchStore.getState();
    searchSnapshotRef.current = {
      query: s.query,
      tokens: s.tokens,
      results: s.results,
      related: s.related,
      hasMore: s.hasMore,
      error: s.error,
    };
  }, [isOpen]);

  // UI Store
  const activeWorkspace = useUIStore((state) => state.activeWorkspace);
  const setActiveWorkspace = useUIStore((state) => state.setActiveWorkspace);
  const setSettingsDialogOpen = useUIStore((state) => state.setSettingsDialogOpen);
  const setComposeOpen = useUIStore((state) => state.setComposeOpen);

  // Email stores
  const selectedEmailId = useEmailStore((state) => state.selectedEmailId);
  const emails = useEmailStore((state) => state.emails);
  const selectedEmail = useMemo(
    () => emails.find((e) => e.id === selectedEmailId) ?? null,
    [emails, selectedEmailId]
  );
  const selectedAccountId = useAccountStore((state) => state.selectedAccountId);

  const { starEmail, markRead } = useEmailActionsStore();

  // Bulk operations
  const bulkSelection = useBulkOperationsStore((state) => state.bulkSelection);
  const bulkEditMode = useBulkOperationsStore((state) => state.bulkEditMode);
  const setBulkEditMode = useBulkOperationsStore((state) => state.setBulkEditMode);
  const applyBulkAction = useBulkOperationsStore((state) => state.applyBulkAction);
  const clearSelection = useBulkOperationsStore((state) => state.clearSelection);

  // Sender grouping
  const activeSenderGroup = useSenderGroupingStore((state) => state.activeSenderGroup);
  const inboxMode = useSenderGroupingStore((state) => state.inboxMode);
  const setInboxMode = useSenderGroupingStore((state) => state.setInboxMode);
  const mailView = useUIStore((state) => state.mailView);
  const showSenderDigest = useSenderGroupingStore((state) => state.showSenderDigest);
  const exitSenderGroup = useSenderGroupingStore((state) => state.exitSenderGroup);
  const activeSenderGroupInfo = useSenderGroupingStore((state) => state.activeSenderGroupInfo);

  // Task stores
  const { setTaskStatus, saveTask } = useTodoActionsStore();
  const selectedTaskId = useTodoViewStore((state) => state.selectedTaskId);
  const setActiveView = useTodoViewStore((state) => state.setActiveView);
  const tasksById = useTodoStore((state) => state.tasks);
  const selectedTask = selectedTaskId ? tasksById[selectedTaskId] ?? null : null;

  // Calendar stores
  const calendarView = useCalendarViewStore((state) => state.activeView);
  const setCalendarView = useCalendarViewStore((state) => state.setActiveView);
  const resetFocusDate = useCalendarViewStore((state) => state.resetFocusDate);
  const openCreateEventDialog = useCalendarModalStore((state) => state.openCreateDialog);

  // Undo/redo availability — reactive, so the palette updates while open
  const canUndoNow = useUndoStore((state) => !state.isProcessing && state.undoStack.length > 0);
  const canRedoNow = useUndoStore((state) => !state.isProcessing && state.redoStack.length > 0);

  // Check if we're in action mode (query starts with / OR no query yet)
  const isActionMode = query.startsWith('/') || !query.trim();
  const actionQuery = query.startsWith('/') ? query.slice(1).trim() : '';
  const searchQuery = query.startsWith('/') ? '' : query.trim();

  // Debounce search query (only when not in action mode)
  useEffect(() => {
    if (!searchQuery) {
      setDebouncedQuery('');
      return;
    }
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 200);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Perform search when debounced query changes (only in search mode).
  // quick: keyword-only type-ahead — the full hybrid (vector) search runs
  // when the user commits via Enter / "View all results".
  useEffect(() => {
    if (debouncedQuery && !isActionMode) {
      void performSearch(debouncedQuery, { skipViewChange: true, quick: true });
    }
  }, [debouncedQuery, isActionMode, performSearch]);

  // Restore state when dismissing (Radix only fires this on ESC/click-outside,
  // never on a programmatic close() from an action — so action selections keep
  // their own state handling).
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        close();
        setQuery('');
        // Restore the pre-palette search snapshot instead of wiping the store
        // (X1/X2): brings back a committed search view's results if there was
        // one, or effectively clears if not — without any selection/group reset.
        const snap = searchSnapshotRef.current;
        useSearchStore.setState((state) => ({
          query: snap?.query ?? '',
          tokens: snap?.tokens ?? [],
          results: snap?.results ?? [],
          related: snap?.related ?? [],
          hasMore: snap?.hasMore ?? false,
          error: snap?.error ?? null,
          loading: false,
          loadingMore: false,
          // Invalidate any in-flight palette search so a late response can't
          // repopulate after the restore.
          requestId: state.requestId + 1,
        }));
      }
    },
    [close]
  );

  // Handle selecting a search result
  const handleSelectResult = useCallback(
    (result: SearchResultItem) => {
      close();
      setQuery('');

      if (result.kind === 'task') {
        navigateToTask(result.task.id);
      } else if (result.kind === 'calendar_event') {
        navigateToCalendarEvent(result.calendar_event);
      } else if (result.kind === 'email') {
        // For email results, select the email first, then switch workspace
        // This ensures the email state is set before MailboxPage renders
        const { email, accountId } = result;
        if (email && accountId) {
          // Clear search and set view to mailbox
          useSearchStore.getState().clearSearch();
          useUIStore.getState().setView('mailbox');
          // Select the email (this will trigger URL update via MailboxPage's sync effect)
          void useEmailStore.getState().selectEmail(email.id, accountId, email.folderId ?? '');
          // Switch to mail workspace if needed (do this last to avoid clearing search again)
          if (activeWorkspace !== 'mail') {
            useUIStore.setState({ activeWorkspace: 'mail', primaryActionContext: 'compose' });
          }
        } else {
          navigateToMail();
        }
      } else if (result.kind === 'thread') {
        // For thread results, select the thread first, then switch workspace
        const { thread, accountId } = result;
        if (thread?.messages?.length && accountId) {
          // Clear search and set view to mailbox
          useSearchStore.getState().clearSearch();
          useUIStore.getState().setView('mailbox');
          // Select the first message in the thread
          const firstMessage = thread.messages[0];
          void useEmailStore.getState().selectEmail(firstMessage.id, accountId, firstMessage.folderId ?? '');
          // Switch to mail workspace if needed
          if (activeWorkspace !== 'mail') {
            useUIStore.setState({ activeWorkspace: 'mail', primaryActionContext: 'compose' });
          }
        } else {
          navigateToMail();
        }
      }
    },
    [activeWorkspace, close]
  );

  // Emails are the ranked list; tasks/events arrive in the separate related block
  const { messageResults, taskResults, calendarResults } = useMemo(() => {
    const tasks: SearchResultItem[] = [];
    const calendar: SearchResultItem[] = [];
    for (const result of relatedResults) {
      if (result.kind === 'task') {
        tasks.push(result);
      } else if (result.kind === 'calendar_event') {
        calendar.push(result);
      }
    }
    return {
      messageResults: searchResults.filter((result) => result.kind !== 'task' && result.kind !== 'calendar_event').slice(0, 5),
      taskResults: tasks.slice(0, 5),
      calendarResults: calendar.slice(0, 5),
    };
  }, [searchResults, relatedResults]);

  // Determine current context for actions
  const hasBulkSelection = bulkSelection.size > 0;
  const isInSenderGroup = activeSenderGroup !== null;
  const hasEmailSelected = selectedEmail !== null && activeWorkspace === 'mail';
  const hasTaskSelected = selectedTask !== null && activeWorkspace === 'todo';

  // ===================
  // CONTEXT-AWARE ACTIONS
  // ===================

  // Mail workspace actions (when no email selected)
  const mailWorkspaceActions = useMemo((): ActionItem[] => {
    if (activeWorkspace !== 'mail') return [];
    if (hasEmailSelected || hasBulkSelection || isInSenderGroup) return [];

    const actions: ActionItem[] = [];

    actions.push({
      id: 'mail-compose',
      label: 'Compose new email',
      icon: Plus,
      shortcut: 'c',
      onSelect: () => {
        setComposeOpen(true);
        close();
      },
    });

    actions.push({
      id: 'mail-refresh',
      label: 'Refresh mailbox',
      icon: RefreshCw,
      onSelect: () => {
        // Read the REAL current selection at execute time — emitting an empty
        // folderId made the reload handler early-return (silent no-op).
        const accountId = useAccountStore.getState().selectedAccountId;
        const folderId = useFolderStore.getState().selectedFolderId();
        if (accountId && folderId) {
          mailEvents.emit('mail:mailbox:reload', {
            accountId,
            folderId,
            options: { reset: true },
          });
        }
        close();
      },
    });

    actions.push({
      id: 'mail-bulk-mode',
      label: bulkEditMode ? 'Exit bulk edit mode' : 'Enter bulk edit mode',
      icon: Edit,
      onSelect: () => {
        setBulkEditMode(!bulkEditMode);
        close();
      },
    });

    const inboxModes = [
      { mode: 'flat' as const, label: 'Inbox: show individual messages' },
      { mode: 'senders' as const, label: 'Inbox: group by sender' },
    ];
    for (const { mode, label } of inboxModes) {
      if (mode === inboxMode) continue;
      actions.push({
        id: `mail-inbox-mode-${mode}`,
        label,
        icon: Users,
        onSelect: () => {
          void setInboxMode(mode);
          close();
        },
      });
    }

    // Mail sub-view tabs
    const mailViews: Array<{ view: MailView; label: string; icon: LucideIcon }> = [
      { view: 'mailbox', label: 'Go to Mailbox', icon: Mail },
      { view: 'briefing', label: 'Go to Briefing', icon: Sparkles },
      { view: 'triage', label: 'Go to Inbox Triage', icon: FileStack },
      { view: 'surveys', label: 'Go to Surveys', icon: Star },
      { view: 'subscriptions', label: 'Go to Subscriptions', icon: MailX },
    ];
    for (const { view, label, icon } of mailViews) {
      if (view === mailView) continue;
      actions.push({
        id: `mail-view-${view}`,
        label,
        icon,
        onSelect: () => {
          navigateToMailView(view);
          close();
        },
      });
    }

    return actions;
  }, [activeWorkspace, hasEmailSelected, hasBulkSelection, isInSenderGroup, bulkEditMode, inboxMode, mailView, setComposeOpen, setBulkEditMode, setInboxMode, close]);

  // Bulk selection actions
  const bulkActions = useMemo((): ActionItem[] => {
    if (activeWorkspace !== 'mail') return [];
    if (!hasBulkSelection && !isInSenderGroup) return [];

    const actions: ActionItem[] = [];
    const count = hasBulkSelection ? bulkSelection.size : (activeSenderGroupInfo?.totalMessages ?? 0);
    const suffix = count > 1 ? ` (${count})` : '';

    // Inside a sender group with NO explicit selection, "all" means the whole
    // sender group — route through the sender-group engine path (which fans out
    // across every account), not applyBulkAction (whose empty selection made
    // these silent no-ops).
    const groupTargeted = !hasBulkSelection && isInSenderGroup;
    const runGroupOrBulk = (
      bulkAction: 'archive' | 'delete' | 'markRead' | 'markUnread'
    ) => {
      if (groupTargeted) {
        const group = useSenderGroupingStore.getState().activeSenderGroupInfo;
        if (group) {
          void useSenderGroupingStore.getState().handleQuickAction(group, bulkAction);
        }
        return;
      }
      void applyBulkAction(bulkAction);
    };

    actions.push({
      id: 'bulk-archive',
      label: `Archive all${suffix}`,
      icon: Archive,
      onSelect: () => {
        runGroupOrBulk('archive');
        close();
      },
    });

    actions.push({
      id: 'bulk-delete',
      label: `Delete all${suffix}`,
      icon: Trash2,
      onSelect: () => {
        runGroupOrBulk('delete');
        close();
      },
    });

    actions.push({
      id: 'bulk-mark-read',
      label: `Mark all read${suffix}`,
      icon: MailOpen,
      onSelect: () => {
        runGroupOrBulk('markRead');
        close();
      },
    });

    actions.push({
      id: 'bulk-mark-unread',
      label: `Mark all unread${suffix}`,
      icon: Mail,
      onSelect: () => {
        runGroupOrBulk('markUnread');
        close();
      },
    });

    if (hasBulkSelection) {
      // Train actions act on the explicit selection only (no sender-group
      // fan-out exists for training) — don't offer a silent no-op.
      actions.push({
        id: 'bulk-train-spam',
        label: `Train as spam${suffix}`,
        icon: XCircle,
        onSelect: () => {
          void applyBulkAction('trainSpam');
          close();
        },
      });

      actions.push({
        id: 'bulk-train-good',
        label: `Train as not spam${suffix}`,
        icon: CheckCircle2,
        onSelect: () => {
          void applyBulkAction('trainGood');
          close();
        },
      });
    }

    if (hasBulkSelection) {
      actions.push({
        id: 'bulk-clear',
        label: 'Clear selection',
        icon: XCircle,
        onSelect: () => {
          clearSelection();
          close();
        },
      });
    }

    if (isInSenderGroup) {
      actions.push({
        id: 'sender-view-digest',
        label: 'View sender digest',
        icon: Eye,
        onSelect: () => {
          if (activeSenderGroupInfo) {
            void showSenderDigest(activeSenderGroupInfo);
          }
          close();
        },
      });

      actions.push({
        id: 'sender-exit',
        label: 'Exit sender group',
        icon: CornerUpLeft,
        onSelect: () => {
          void exitSenderGroup();
          close();
        },
      });
    }

    return actions;
  }, [activeWorkspace, hasBulkSelection, isInSenderGroup, bulkSelection.size, activeSenderGroupInfo, applyBulkAction, clearSelection, showSenderDigest, exitSenderGroup, close]);

  // Email detail actions
  const emailDetailActions = useMemo((): ActionItem[] => {
    if (activeWorkspace !== 'mail') return [];
    if (!hasEmailSelected || !selectedEmail || !selectedAccountId) return [];

    const actions: ActionItem[] = [];

    actions.push({
      id: 'email-star',
      label: selectedEmail.starred ? 'Unstar email' : 'Star email',
      icon: Star,
      shortcut: 's',
      onSelect: () => {
        void starEmail(selectedEmail.id, selectedAccountId, selectedEmail.folderId ?? '');
        close();
      },
    });

    actions.push({
      id: 'email-archive',
      label: 'Archive email',
      icon: Archive,
      shortcut: 'e',
      // Same target resolution as the `e` key: the whole open conversation
      // for a thread, the single email otherwise.
      onSelect: () => {
        close();
        void keyboardRegistry.get('email:archive')?.handler();
      },
    });

    actions.push({
      id: 'email-delete',
      label: 'Delete email',
      icon: Trash2,
      shortcut: '#',
      onSelect: () => {
        close();
        void keyboardRegistry.get('email:delete')?.handler();
      },
    });

    actions.push({
      id: 'email-read',
      label: selectedEmail.read ? 'Mark as unread' : 'Mark as read',
      icon: selectedEmail.read ? MailOpen : Mail,
      shortcut: 'u',
      onSelect: () => {
        void markRead(selectedEmail.id, selectedAccountId, selectedEmail.folderId ?? '', !selectedEmail.read);
        close();
      },
    });

    return actions;
  }, [activeWorkspace, hasEmailSelected, selectedEmail, selectedAccountId, starEmail, markRead, close]);

  // Task workspace navigation actions
  const taskWorkspaceActions = useMemo((): ActionItem[] => {
    if (activeWorkspace !== 'todo') return [];

    const actions: ActionItem[] = [];

    actions.push({
      id: 'task-new',
      label: 'Create new task',
      icon: Plus,
      onSelect: () => {
        useUIStore.getState().triggerPrimaryAction();
        close();
      },
    });

    const views: Array<{ id: string; view: TodoPrimaryView; label: string }> = [
      { id: 'task-nav-inbox', view: 'inbox', label: 'Go to Inbox' },
      { id: 'task-nav-today', view: 'today', label: 'Go to Today' },
      { id: 'task-nav-upcoming', view: 'upcoming', label: 'Go to Upcoming' },
      { id: 'task-nav-anytime', view: 'anytime', label: 'Go to Anytime' },
      { id: 'task-nav-someday', view: 'someday', label: 'Go to Someday' },
      { id: 'task-nav-logbook', view: 'logbook', label: 'Go to Logbook' },
      { id: 'task-nav-trash', view: 'trash', label: 'Go to Trash' },
    ];

    views.forEach(({ id, view, label }) => {
      actions.push({
        id,
        label,
        icon: ListTodo,
        onSelect: () => {
          setActiveView(view);
          close();
        },
      });
    });

    return actions;
  }, [activeWorkspace, setActiveView, close]);

  // Task selected actions
  const taskSelectedActions = useMemo((): ActionItem[] => {
    if (activeWorkspace !== 'todo') return [];
    if (!hasTaskSelected || !selectedTask) return [];

    const actions: ActionItem[] = [];

    if (selectedTask.status === 'open') {
      actions.push({
        id: 'task-complete',
        label: 'Complete task',
        icon: CheckCircle2,
        shortcut: 'c',
        onSelect: () => {
          void setTaskStatus(selectedTask.id, 'completed');
          close();
        },
      });

      actions.push({
        id: 'task-cancel',
        label: 'Cancel task',
        icon: XCircle,
        onSelect: () => {
          void setTaskStatus(selectedTask.id, 'cancelled');
          close();
        },
      });

      actions.push({
        id: 'task-someday',
        label: selectedTask.isSomeday ? 'Make active' : 'Move to someday',
        icon: selectedTask.isSomeday ? Sun : Moon,
        onSelect: () => {
          void saveTask(selectedTask.id, { isSomeday: !selectedTask.isSomeday });
          close();
        },
      });

      actions.push({
        id: 'task-postpone',
        label: 'Postpone task',
        icon: Clock,
        onSelect: () => {
          // Emit postpone event
          window.dispatchEvent(new CustomEvent('todo:command', { detail: { type: 'postpone_selection' } }));
          close();
        },
      });
    }

    actions.push({
      id: 'task-delete',
      label: 'Delete task',
      icon: Trash2,
      onSelect: () => {
        void setTaskStatus(selectedTask.id, 'deleted');
        close();
      },
    });

    return actions;
  }, [activeWorkspace, hasTaskSelected, selectedTask, setTaskStatus, saveTask, close]);

  // Calendar workspace actions
  const calendarWorkspaceActions = useMemo((): ActionItem[] => {
    if (activeWorkspace !== 'calendar') return [];

    const actions: ActionItem[] = [];

    actions.push({
      id: 'cal-new-event',
      label: 'Create new event',
      icon: Plus,
      onSelect: () => {
        openCreateEventDialog();
        close();
      },
    });

    actions.push({
      id: 'cal-go-today',
      label: 'Go to today',
      icon: Calendar,
      onSelect: () => {
        resetFocusDate();
        close();
      },
    });

    const viewOptions: Array<{ id: string; view: 'day' | 'week' | 'month' | 'year' | 'agenda'; label: string; icon: LucideIcon }> = [
      { id: 'cal-view-day', view: 'day', label: 'Day view', icon: CalendarDays },
      { id: 'cal-view-week', view: 'week', label: 'Week view', icon: CalendarRange },
      { id: 'cal-view-month', view: 'month', label: 'Month view', icon: Grid3X3 },
      { id: 'cal-view-agenda', view: 'agenda', label: 'Agenda view', icon: List },
    ];

    viewOptions.forEach(({ id, view, label, icon }) => {
      if (calendarView !== view) {
        actions.push({
          id,
          label,
          icon,
          onSelect: () => {
            setCalendarView(view);
            close();
          },
        });
      }
    });

    return actions;
  }, [activeWorkspace, calendarView, openCreateEventDialog, resetFocusDate, setCalendarView, close]);

  // Global navigation actions (always available)
  const globalActions = useMemo((): ActionItem[] => {
    const actions: ActionItem[] = [];

    // Show workspace navigation for non-active workspaces
    const workspaces: Array<{ id: string; workspace: Workspace; label: string; icon: LucideIcon }> = [
      { id: 'go-mail', workspace: 'mail', label: 'Go to Mail', icon: Mail },
      { id: 'go-tasks', workspace: 'todo', label: 'Go to Tasks', icon: CheckCircle2 },
      { id: 'go-calendar', workspace: 'calendar', label: 'Go to Calendar', icon: Calendar },
    ];

    workspaces.forEach(({ id, workspace, label, icon }) => {
      if (activeWorkspace !== workspace) {
        actions.push({
          id,
          label,
          icon,
          onSelect: () => {
            setActiveWorkspace(workspace);
            close();
          },
        });
      }
    });

    actions.push({
      id: 'settings',
      label: 'Open settings',
      icon: Settings,
      shortcut: ',',
      onSelect: () => {
        setSettingsDialogOpen(true);
        close();
      },
    });

    // Undo/Redo: availability is SUBSCRIBED (canUndoNow/canRedoNow) so the
    // items appear/disappear live while the palette is open, and execution
    // reads the store at click time — never a memo-time snapshot.
    if (canUndoNow) {
      actions.push({
        id: 'global-undo',
        label: 'Undo',
        icon: Undo2,
        shortcut: '⌘Z',
        onSelect: () => {
          void useUndoStore.getState().undo();
          close();
        },
      });
    }

    if (canRedoNow) {
      actions.push({
        id: 'global-redo',
        label: 'Redo',
        icon: Redo2,
        shortcut: '⌘⇧Z',
        onSelect: () => {
          void useUndoStore.getState().redo();
          close();
        },
      });
    }

    return actions;
  }, [activeWorkspace, canUndoNow, canRedoNow, setActiveWorkspace, setSettingsDialogOpen, close]);

  // Combine all context actions
  const contextActions = useMemo(() => {
    return [
      ...mailWorkspaceActions,
      ...bulkActions,
      ...emailDetailActions,
      ...taskWorkspaceActions,
      ...taskSelectedActions,
      ...calendarWorkspaceActions,
    ];
  }, [mailWorkspaceActions, bulkActions, emailDetailActions, taskWorkspaceActions, taskSelectedActions, calendarWorkspaceActions]);

  // Filter actions by query (only in action mode)
  const filteredContextActions = useMemo(() => {
    if (!isActionMode) return [];
    if (!actionQuery) return contextActions;
    const lowerQuery = actionQuery.toLowerCase();
    return contextActions.filter((action) =>
      action.label.toLowerCase().includes(lowerQuery)
    );
  }, [contextActions, actionQuery, isActionMode]);

  const filteredGlobalActions = useMemo(() => {
    if (!isActionMode) return [];
    if (!actionQuery) return globalActions;
    const lowerQuery = actionQuery.toLowerCase();
    return globalActions.filter((action) =>
      action.label.toLowerCase().includes(lowerQuery)
    );
  }, [globalActions, actionQuery, isActionMode]);

  // Only show search results when not in action mode
  const showSearchResults = !isActionMode;

  // "View all results" handler - opens full search view
  const handleViewAllResults = useCallback(() => {
    if (!searchQuery) return;
    close();
    setQuery('');

    // Clear any selected email/digest so the search results list is visible (not covered by detail pane)
    useEmailStore.getState().clearSelection();
    useSenderGroupingStore.getState().closeSenderDigest();

    // Set up the search view state BEFORE switching workspace
    // This ensures MailboxPage sees view='search' when it mounts
    // We can't rely on performSearch to set this because it returns early if selectedAccountId is null
    // Bump requestId so any in-flight palette search can't repopulate the view
    // we are about to hand to performSearch (F17).
    useSearchStore.setState((state) => ({
      query: searchQuery,
      loading: true,
      results: [],
      error: null,
      requestId: state.requestId + 1,
    }));
    useUIStore.getState().setView('search');

    // Update URL with search query parameter BEFORE switching workspace
    // This ensures MailboxPage's mount effect sees the search query in the URL
    const searchUrl = `#/?q=${encodeURIComponent(searchQuery)}`;
    safePushState(searchUrl);

    // Switch to mail workspace if needed (do this after URL update so MailboxPage sees the search query)
    if (activeWorkspace !== 'mail') {
      useUIStore.setState({ activeWorkspace: 'mail', primaryActionContext: 'compose' });
    }

    // Now perform the search - results will populate when ready
    void performSearch(searchQuery, { skipViewChange: true });
  }, [activeWorkspace, close, performSearch, searchQuery]);

  // Handle Enter key in search mode to immediately navigate to results
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && !isActionMode && searchQuery.trim()) {
        // Only trigger if we're in search mode and have a query
        e.preventDefault();
        handleViewAllResults();
      }
    },
    [handleViewAllResults, isActionMode, searchQuery]
  );

  // When opened from the header search input, the panel docks onto the input
  // (right-aligned, its own input replacing the header one) instead of
  // centering — ⌘K and the mobile search icon keep the centered dialog.
  const anchoredStyle: React.CSSProperties | undefined = anchorRect
    ? {
        top: Math.max(8, anchorRect.top - 6),
        left: Math.max(8, anchorRect.right - Math.max(anchorRect.width, 576)),
        width: Math.max(anchorRect.width, 576),
        // Tailwind v4's translate utilities use the `translate` property, so
        // both must be neutralized for the fixed coordinates to hold.
        transform: 'none',
        translate: 'none',
      }
    : undefined;

  return (
    <CommandDialog
      open={isOpen}
      onOpenChange={handleOpenChange}
      title="Command Palette"
      description="Search or run a command"
      className={
        anchorRect
          ? 'max-w-xl w-full data-[state=open]:slide-in-from-top-1 data-[state=closed]:slide-out-to-top-1'
          : 'max-w-xl w-full max-md:top-[calc(env(safe-area-inset-top,0px)+1rem)] max-md:translate-y-0'
      }
      contentStyle={anchoredStyle}
      shouldFilter={false}
      modal={false}
    >
      <CommandInput
        placeholder="Search messages, tasks, events... (/ for actions)"
        value={query}
        onValueChange={setQuery}
        onKeyDown={handleKeyDown}
      />
      <CommandList className="max-h-[400px]">
        <CommandEmpty>
          {isActionMode
            ? 'No matching actions.'
            : searchLoading
              ? 'Searching...'
              : 'No results found.'}
        </CommandEmpty>

        {/* View all results - shown when search has results */}
        {showSearchResults && searchResults.length > 0 && (
          <CommandGroup heading="Search">
            <CommandItem onSelect={handleViewAllResults}>
              <ExternalLink className="mr-2 h-4 w-4 text-muted-foreground" />
              <span>View all {searchResults.length} results</span>
            </CommandItem>
          </CommandGroup>
        )}

        {/* Search Results - only shown when not in action mode */}
        {showSearchResults && messageResults.length > 0 && (
          <CommandGroup heading="Messages">
            {messageResults.map((result) => {
              const email = result.kind === 'email' ? result.email : null;
              const thread = result.kind === 'thread' ? result.thread : null;
              const subject = email?.subject ?? thread?.subject ?? '(no subject)';
              const snippet = email?.snippet ?? thread?.snippet;
              const key = email?.id ?? thread?.id ?? result.kind;

              return (
                <CommandItem
                  key={key}
                  onSelect={() => handleSelectResult(result)}
                >
                  <Mail className="mr-2 h-4 w-4 text-muted-foreground" />
                  <div className="flex flex-col min-w-0 flex-1">
                    <span className="truncate font-medium">
                      {subject || '(no subject)'}
                    </span>
                    {snippet && (
                      <span className="truncate text-xs text-muted-foreground">
                        {snippet}
                      </span>
                    )}
                  </div>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}

        {showSearchResults && taskResults.length > 0 && (
          <CommandGroup heading="Tasks">
            {taskResults.map((result) => (
              <CommandItem
                key={result.kind === 'task' ? result.task.id : 'task'}
                onSelect={() => handleSelectResult(result)}
              >
                <CheckCircle2 className="mr-2 h-4 w-4 text-muted-foreground" />
                <div className="flex flex-col min-w-0 flex-1">
                  <span className="truncate font-medium">
                    {result.kind === 'task' ? result.task.title : 'Task'}
                  </span>
                  {result.kind === 'task' && result.task.dueDate && (
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {result.task.dueDate}
                    </span>
                  )}
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {showSearchResults && calendarResults.length > 0 && (
          <CommandGroup heading="Events">
            {calendarResults.map((result) => (
              <CommandItem
                key={result.kind === 'calendar_event' ? result.calendar_event.id : 'event'}
                onSelect={() => handleSelectResult(result)}
              >
                <Calendar className="mr-2 h-4 w-4 text-muted-foreground" />
                <div className="flex flex-col min-w-0 flex-1">
                  <span className="truncate font-medium">
                    {result.kind === 'calendar_event'
                      ? result.calendar_event.summary || '(no title)'
                      : 'Event'}
                  </span>
                  {result.kind === 'calendar_event' && (
                    <span className="text-xs text-muted-foreground">
                      {result.calendar_event.startDateTime
                        ? new Date(result.calendar_event.startDateTime).toLocaleDateString()
                        : result.calendar_event.startDate}
                    </span>
                  )}
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {/* Context Actions */}
        {filteredContextActions.length > 0 && (
          <CommandGroup heading="Actions">
            {filteredContextActions.map((action) => (
              <CommandItem key={action.id} onSelect={action.onSelect}>
                <action.icon className="mr-2 h-4 w-4 text-muted-foreground" />
                <span>{action.label}</span>
                {action.shortcut && (
                  <CommandShortcut>{action.shortcut}</CommandShortcut>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {/* Global Actions */}
        {filteredGlobalActions.length > 0 && (
          <CommandGroup heading={filteredContextActions.length > 0 ? 'Navigation' : 'Actions'}>
            {filteredGlobalActions.map((action) => (
              <CommandItem key={action.id} onSelect={action.onSelect}>
                <action.icon className="mr-2 h-4 w-4 text-muted-foreground" />
                <span>{action.label}</span>
                {action.shortcut && (
                  <CommandShortcut>{action.shortcut}</CommandShortcut>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}
